/**
 * The admin's own profile: their timezone, which dates their games' public
 * results links (games.swtorbit.com/YYYY-MM-DD/CODE uses the day the game
 * first reached its lobby, in this timezone).
 */

import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@swtorbit/ui/react';
import { browserTimezone, browserTimezones, getAdminProfile, updateAdminTimezone } from '../services/adminProfileApi';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UTC = '';

/** Today's date in a timezone, as the results links write it. */
function todayIn(timezone: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: timezone ?? 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  } catch {
    return '';
  }
}

export function ProfileModal({ isOpen, onClose }: ProfileModalProps) {
  const [saved, setSaved] = useState<string | null>(null);
  const [choice, setChoice] = useState<string>(UTC);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const detected = useMemo(browserTimezone, []);

  const options = useMemo(() => {
    const zones = new Set(browserTimezones());
    // Keep the saved and detected zones listed even if the browser's list lacks them.
    for (const zone of [saved, detected]) if (zone) zones.add(zone);
    return [...zones].sort();
  }, [saved, detected]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setNotice(null);
    setLoading(true);
    getAdminProfile()
      .then((profile) => {
        setSaved(profile.timezone);
        setChoice(profile.timezone ?? UTC);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const profile = await updateAdminTimezone(choice === UTC ? null : choice);
      setSaved(profile.timezone);
      setNotice('Saved. Results links made from now on use it; links already made keep their date.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const current = choice === UTC ? null : choice;
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Your profile"
      size="sm"
      footer={
        <>
          <button className="btn btn--outline btn--slate" onClick={onClose}>
            Close
          </button>
          <button className="btn btn--cyan" disabled={loading || saving || current === saved} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="d-flex flex-column gap-sm">
        <div className="field">
          <label htmlFor="profile-timezone">Timezone</label>
          <select id="profile-timezone" className="select" value={choice} disabled={loading} onChange={(e) => setChoice(e.target.value)}>
            <option value={UTC}>UTC</option>
            {options.map((zone) => (
              <option key={zone} value={zone}>
                {zone}
                {zone === detected ? ' (this browser)' : ''}
              </option>
            ))}
          </select>
        </div>
        <p className="fs-sm text-slate m-0">
          Your games' public results links are dated by the day the game first reached its lobby, in this timezone. A
          game reaching its lobby now would be dated <b className="text-white">{todayIn(current)}</b>.
        </p>
        {error && <p className="fs-sm text-red m-0">{error}</p>}
        {notice && <p className="fs-sm text-green m-0">{notice}</p>}
      </div>
    </Modal>
  );
}
